"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useDebounce } from "@/hooks/use-debounce";
import { useLocation } from "@/hooks/use-location";
import { Locale } from "@/i18n";
import { cn } from "@/lib/utils";
import {
  ChevronDown,
  Clock,
  Coffee,
  Crosshair,
  Filter,
  Loader2,
  MapPin,
  Search,
  Utensils,
  X
} from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";

type TranslationKey = "en" | "fr" | "ar";

const translations: Record<TranslationKey, Record<string, string>> = {
  en: {
    selectCity: "Select your city or area *",
    searchPlaceholder: "Restaurant name or cuisine",
    useCurrentLocation: "Use my current location",
    loading: "Loading...",
    noCitiesFound: "No cities found",
    search: "Search",
    filters: "Filters",
    selected: "selected",
    clearAll: "Clear All",
    applyFilters: "Apply Filters",
    restaurant: "Restaurant",
    cafe: "Cafe",
    openNow: "Open Now",
    placeType: "Place Type",
    status: "Status",
    priceRange: "Price Range",
    anyPrice: "Any Price",
    priceCheap: "$ (Cheap)",
    priceModerate: "$$ (Moderate)",
    priceExpensive: "$$$ (Expensive)",
    rating: "Rating",
    anyRating: "Any Rating",
    rating4plus: "4+ stars",
    rating3plus: "3+ stars",
    rating2plus: "2+ stars",
  },
  fr: {
    selectCity: "Sélectionnez votre ville ou zone *",
    searchPlaceholder: "Nom du restaurant ou cuisine",
    useCurrentLocation: "Utiliser ma position actuelle",
    loading: "Chargement...",
    noCitiesFound: "Aucune ville trouvée",
    search: "Rechercher",
    filters: "Filtres",
    selected: "sélectionné",
    clearAll: "Tout effacer",
    applyFilters: "Appliquer les filtres",
    restaurant: "Restaurant",
    cafe: "Café",
    openNow: "Ouvert maintenant",
    placeType: "Type d'établissement",
    status: "Statut",
    priceRange: "Gamme de Prix",
    anyPrice: "Tous les prix",
    priceCheap: "$ (Abordable)",
    priceModerate: "$$ (Modéré)",
    priceExpensive: "$$$ (Cher)",
    rating: "Note",
    anyRating: "Toutes les notes",
    rating4plus: "4+ étoiles",
    rating3plus: "3+ étoiles",
    rating2plus: "2+ étoiles",
  },
  ar: {
    selectCity: "اختر مدينتك أو منطقتك *",
    searchPlaceholder: "اسم المطعم أو نوع المطبخ",
    useCurrentLocation: "استخدم موقعي الحالي",
    loading: "جارٍ التحميل...",
    noCitiesFound: "لم يتم العثور على مدن",
    search: "بحث",
    filters: "تصفية",
    selected: "محدد",
    clearAll: "مسح الكل",
    applyFilters: "تطبيق التصفية",
    restaurant: "مطعم",
    cafe: "مقهى",
    openNow: "مفتوح الآن",
    placeType: "نوع المكان",
    status: "الحالة",
    priceRange: "نطاق السعر",
    anyPrice: "أي سعر",
    priceCheap: "$ (رخيص)",
    priceModerate: "$$ (متوسط)",
    priceExpensive: "$$$ (باهظ الثمن)",
    rating: "تقييم",
    anyRating: "أي تقييم",
    rating4plus: "4+ نجوم",
    rating3plus: "3+ نجوم",
    rating2plus: "2+ نجوم",
  }
};

interface FiltersConfig {
  placeType: Array<{ id: string; icon: JSX.Element; label: string; }>;
  status: Array<{ id: string; icon: JSX.Element; label: string; }>;
  priceRange: Array<{ id: string; icon?: JSX.Element; label: string; value: string }>;
  rating: Array<{ id: string; icon?: JSX.Element; label: string; value: number }>;
}

const createFilters = (t: Record<string, string>): FiltersConfig => ({
  placeType: [
    { id: "restaurant", icon: <Utensils className="w-4 h-4" />, label: t.restaurant },
    { id: "cafe", icon: <Coffee className="w-4 h-4" />, label: t.cafe },
  ],
  status: [
    { id: "open", icon: <Clock className="w-4 h-4" />, label: t.openNow },
  ],
  priceRange: [
    // { id: "anyPrice", label: t.anyPrice, value: "" }, // An 'Any' option can be implicit or explicit
    { id: "priceCheap", label: t.priceCheap, value: "$" },
    { id: "priceModerate", label: t.priceModerate, value: "$$" },
    { id: "priceExpensive", label: t.priceExpensive, value: "$$$" },
  ],
  rating: [
    // { id: "anyRating", label: t.anyRating, value: 0 }, // An 'Any' option
    { id: "rating4plus", label: t.rating4plus, value: 4 },
    { id: "rating3plus", label: t.rating3plus, value: 3 },
    { id: "rating2plus", label: t.rating2plus, value: 2 },
  ]
});

interface City {
  id: string;
  name: string;
  slug: string;
}

interface SearchProps {
  variant?: "global" | "nav";
  className?: string;
  locale?: Locale;
}

// Cache cities to avoid refetching
let citiesCache: City[] = [];
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function UnifiedSearch({
  variant = "global",
  className,
  locale = "fr"
}: SearchProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [showCities, setShowCities] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  const [locationInput, setLocationInput] = useState(searchParams.get("location") || "");
  const [cities, setCities] = useState<City[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string | number>>({
    type: searchParams.get("type") || "",
    status: searchParams.get("status") || "",
    price_range: searchParams.get("price_range") || "",
    min_rating: Number(searchParams.get("min_rating")) || 0,
  });
  const [isSearching, setIsSearching] = useState(false);
  const [prefetchedUrl, setPrefetchedUrl] = useState<string | null>(null);

  const debouncedLocationInput = useDebounce(locationInput, 300);

  const { getLocation, isLoadingLocation, location: userLocation, cityName } = useLocation();
  const t = translations[locale as TranslationKey];
  const FILTERS = createFilters(t);
  const isRTL = locale === "ar";

  // Memoize filtered cities for better performance
  const filteredCities = useMemo(() => {
    if (!debouncedLocationInput) return cities;
    const searchTerm = debouncedLocationInput.toLowerCase();
    return cities.filter(city =>
      city.name.toLowerCase().includes(searchTerm)
    );
  }, [cities, debouncedLocationInput]);

  // Prepare search URL for prefetching
  const prepareSearchUrl = useCallback(() => {
    if (!location) return null;

    const params = new URLSearchParams();
    if (search) params.append("q", search);
    if (location) params.append("location", location);

    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });

    if (userLocation) {
      params.append("lat", userLocation.latitude.toString());
      params.append("lon", userLocation.longitude.toString());
    }

    return `/search?${params.toString()}`;
  }, [location, search, selectedFilters, userLocation]);

  // Prefetch search results when params change
  useEffect(() => {
    if (typeof window !== 'undefined' && location) {
      const url = prepareSearchUrl();
      if (url && url !== prefetchedUrl) {
        try {
          // @ts-ignore - Next.js router types are not fully correct for prefetch with string
          router.prefetch(url);
          setPrefetchedUrl(url);
        } catch (error) {
          console.error("Prefetch error:", error);
        }
      }
    }
  }, [router, prepareSearchUrl, location, prefetchedUrl]);

  useEffect(() => {
    const fetchCities = async () => {
      const now = Date.now();
      // Use cached data if available and fresh
      if (citiesCache.length > 0 && now - lastFetchTime < CACHE_DURATION) {
        setCities(citiesCache);
        return;
      }

      setIsLoadingCities(true);
      try {
        const response = await fetch("https://infra.douq.ma/api/v1/cities");
        const data = await response.json();
        const citiesWithSlugs = data.map((city: any) => ({
          ...city,
          slug: city.name.toLowerCase().replace(/\s+/g, "-")
        }));
        setCities(citiesWithSlugs);

        // Update cache
        citiesCache = citiesWithSlugs;
        lastFetchTime = now;
      } catch (error) {
        console.error("Error fetching cities:", error);
        // Use cached data as fallback even if expired
        if (citiesCache.length > 0) {
          setCities(citiesCache);
        }
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, []);

  const handleSearch = useCallback(async () => {
    if (!location) return;

    setIsSearching(true);
    const url = prepareSearchUrl();

    if (url) {
      try {
        // @ts-ignore - Next.js router types may be strict with dynamic URLs
        await router.push(url);
      } finally {
        setIsSearching(false);
      }
    } else {
      setIsSearching(false);
    }
  }, [location, router, prepareSearchUrl]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (showCities && filteredCities.length > 0) {
        setLocation(filteredCities[0].name);
        setLocationInput(filteredCities[0].name);
        setShowCities(false);
      } else if (location) {
        handleSearch();
      }
    }
  }, [showCities, filteredCities, location, handleSearch]);

  const handleLocationSearch = useCallback(async () => {
    await getLocation();
    if (cityName) {
      const citySlug = cityName.toLowerCase().replace(/\s+/g, "-");
      const matchedCity = cities.find(city => city.slug === citySlug);
      if (matchedCity) {
        setLocation(matchedCity.name);
        setLocationInput(matchedCity.name);
      } else {
        setLocation(cityName);
        setLocationInput(cityName);
      }
      setShowCities(false);
    }
  }, [getLocation, cityName, cities]);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (selectedFilters.type) count++;
    if (selectedFilters.status) count++;
    if (selectedFilters.price_range) count++;
    if (typeof selectedFilters.min_rating === 'number' && selectedFilters.min_rating > 0) count++;
    return count;
  }, [selectedFilters]);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({
      type: "",
      status: "",
      price_range: "",
      min_rating: 0
    });
  }, []);

  const containerStyles = cn(
    "w-full",
    variant === "global" ? "bg-transparent" : "max-w-3xl mx-auto p-4",
    isRTL ? "rtl" : "ltr",
    className
  );

  return (
    <div className={containerStyles}>
      {variant === "global" ? (
        <div className={cn(
          "bg-white dark:bg-slate-800 rounded-2xl shadow-xl dark:shadow-slate-700/50 border border-slate-200/80 dark:border-slate-700 p-6 space-y-5",
          isRTL ? "rtl" : "ltr"
        )}>
          <div className="space-y-4">
            <Popover open={showCities} onOpenChange={setShowCities}>
              <PopoverTrigger asChild>
                <div className="relative group">
                  <MapPin className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-hover:text-emerald-500 dark:text-slate-500 dark:group-hover:text-emerald-500 transition-colors pointer-events-none",
                    isRTL ? "right-3.5" : "left-3.5"
                  )} />
                  <Input
                    value={locationInput}
                    onChange={(e) => {
                      setLocationInput(e.target.value);
                      if (!showCities) setShowCities(true);
                    }}
                    onKeyDown={handleKeyPress}
                    placeholder={t.selectCity}
                    className={cn(
                      "h-14 text-black rounded-xl",
                      isRTL ? "text-right pr-10 pl-10" : "pl-10 pr-10",
                      (isLoadingCities || isLoadingLocation) && "animate-pulse"
                    )}
                  />
                  {locationInput && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocationInput("");
                        setLocation("");
                      }}
                      className={cn(
                        "absolute top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-400 hover:text-emerald-600 dark:text-slate-500 dark:hover:text-emerald-500 hover:bg-emerald-100/50 dark:hover:bg-slate-700/50 rounded-lg transition-colors",
                        isRTL ? "left-2" : "right-2"
                      )}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className={cn(
                  "w-[var(--radix-popover-trigger-width)] p-0 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl mt-1",
                  isRTL ? "rtl" : "ltr"
                )}
                align="start"
                side="bottom"
                sideOffset={4}
              >
                <Command className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md">
                  <CommandList>
                    <CommandGroup>
                      <CommandItem
                        onSelect={handleLocationSearch}
                        className="flex items-center gap-3 py-4 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-700/60 text-slate-700 dark:text-slate-300"
                      >
                        <Crosshair className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-base">{t.useCurrentLocation}</span>
                      </CommandItem>
                      {isLoadingCities || isLoadingLocation ? (
                        <CommandItem disabled className="h-14 flex items-center justify-center py-4">
                          <div className="flex items-center space-x-2">
                            <Loader2 className="w-5 h-5 animate-spin text-emerald-600 dark:text-emerald-400" />
                            <span className="text-base">
                              {t.loading}
                            </span>
                          </div>
                        </CommandItem>
                      ) : filteredCities.map(city => (
                        <CommandItem
                          key={city.id}
                          onSelect={() => {
                            setLocation(city.name);
                            setLocationInput(city.name);
                            setShowCities(false);
                          }}
                          className="flex items-center gap-3 cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-700/60 text-slate-700 dark:text-slate-300 py-4"
                        >
                          <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-base">
                            {city.name}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandEmpty>
                      <div className="flex flex-col items-center py-6 text-slate-500 dark:text-slate-400 text-base">
                        <span>
                          {t.noCitiesFound}
                        </span>
                      </div>
                    </CommandEmpty>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <div className="relative group">
              <Search className={cn(
                "absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-hover:text-emerald-500 dark:text-slate-500 dark:group-hover:text-emerald-500 transition-colors pointer-events-none",
                isRTL ? "right-3.5" : "left-3.5"
              )} />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t.searchPlaceholder}
                className={cn(
                  "h-14 text-gray-800 rounded-xl",
                  isRTL ? "text-right pr-10 pl-10" : "pl-10 pr-10"
                )}
              />
              {search && (
                <button
                  type="button"
                  onClick={(e) => setSearch("")}
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center text-slate-400 hover:text-emerald-600 dark:text-slate-500 dark:hover:text-emerald-500 hover:bg-emerald-100/50 dark:hover:bg-slate-700/50 rounded-lg transition-colors",
                    isRTL ? "left-2" : "right-2"
                  )}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <Button
              onClick={handleSearch}
              disabled={!location || isSearching}
              className={cn(
                "w-full md:flex-1 h-14 text-base rounded-xl font-medium transition-all",
                "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 dark:focus-visible:ring-emerald-500 dark:focus-visible:ring-offset-slate-800",
                isSearching && "cursor-not-allowed",
                !location && "opacity-70"
              )}
            >
              {isSearching ? (
                <Loader2 className={cn(
                  "animate-spin",
                  isRTL ? "ml-2" : "mr-2",
                  "w-5 h-5"
                )} />
              ) : (
                <Search className={cn(
                  isRTL ? "ml-2" : "mr-2",
                  "w-5 h-5"
                )} />
              )}
              {t.search}
            </Button>

            <Button
              variant="outline"
              onClick={() => setShowFilters(true)}
              className={cn(
                "w-full md:flex-1 h-14 text-base rounded-xl transition-all",
                "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200",
                "hover:border-emerald-500/70 hover:text-emerald-600 dark:hover:border-emerald-500/70 dark:hover:text-emerald-300",
                "focus-visible:ring-1 focus-visible:ring-emerald-500 focus-visible:border-emerald-500 dark:focus-visible:ring-emerald-500 dark:focus-visible:border-emerald-500",
                getActiveFiltersCount() > 0 && "bg-emerald-50/80 border-emerald-500/80 text-emerald-600 dark:bg-emerald-900/30 dark:border-emerald-500/70 dark:text-emerald-300"
              )}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-slate-500 group-hover:text-emerald-600 dark:text-slate-400 dark:group-hover:text-emerald-300 transition-colors" />
                  <span>{t.filters}</span>
                  {getActiveFiltersCount() > 0 && (
                    <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 text-xs rounded-full">{getActiveFiltersCount()}</Badge>
                  )}
                </div>
                <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              </div>
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative flex items-center bg-white dark:bg-slate-800 rounded-full shadow-md border border-emerald-200/50 dark:border-emerald-800/50 p-1.5">
          <div className="relative flex-1 min-w-[120px]">
            <MapPin className={cn(
              "absolute top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600 dark:text-emerald-400",
              isRTL ? "right-3" : "left-3"
            )} />
            <Input
              value={locationInput}
              onChange={(e) => {
                setLocationInput(e.target.value);
                if (!showCities) setShowCities(true);
              }}
              onMouseDown={() => {
                if (!showCities) setShowCities(true);
              }}
              onFocus={() => {
                if (!showCities) setShowCities(true);
              }}
              onKeyDown={handleKeyPress}
              placeholder={t.selectCity}
              className={cn(
                "h-10 text-sm bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                "text-emerald-800 dark:text-emerald-100 placeholder:text-emerald-600/70 dark:placeholder:text-emerald-400/80",
                isRTL ? "text-right pr-3 pl-3 mr-7" : "text-left pl-3 pr-3 ml-7",
                (isLoadingCities || isLoadingLocation) && "animate-pulse"
              )}
            />
            {locationInput && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLocationInput("");
                  setLocation("");
                }}
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center",
                  "text-emerald-500 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-200",
                  "rounded-full hover:bg-emerald-100/50 dark:hover:bg-emerald-700/30",
                  isRTL ? "left-2" : "right-2"
                )}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="w-px h-5 bg-emerald-200 dark:bg-emerald-700/50 mx-1.5"></div>

          <div className="relative flex-1 min-w-[120px]">
            <Search className={cn(
              "absolute top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600 dark:text-emerald-400",
              isRTL ? "right-3" : "left-3"
            )} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={t.searchPlaceholder}
              className={cn(
                "h-10 text-sm bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                "text-emerald-800 dark:text-emerald-100 placeholder:text-emerald-600/70 dark:placeholder:text-emerald-400/80",
                isRTL ? "text-right pr-3 pl-3 mr-7" : "text-left pl-3 pr-3 ml-7"
              )}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center",
                  "text-emerald-500 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-200",
                  "rounded-full hover:bg-emerald-100/50 dark:hover:bg-emerald-700/30",
                  isRTL ? "left-2" : "right-2"
                )}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="w-px h-5 bg-emerald-200 dark:bg-emerald-700/50 mx-1.5"></div>

          <button
            type="button"
            onClick={handleSearch}
            disabled={!location || isSearching}
            className={cn(
              "h-10 px-4 rounded-full flex items-center gap-1.5 text-sm font-medium transition-all",
              "bg-emerald-500 text-white hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700",
              !location && "opacity-60 cursor-not-allowed"
            )}
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span>{t.search}</span>
          </button>

          <button
            type="button"
            onClick={() => setShowFilters(true)}
            className={cn(
              "ml-1 h-10 w-10 rounded-full flex items-center justify-center transition-all",
              getActiveFiltersCount() > 0
                ? "bg-emerald-500 text-white hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                : "bg-emerald-100 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-700/50 dark:text-emerald-300 dark:hover:bg-emerald-700/80"
            )}
          >
            <Filter className="h-4 w-4" />
            {getActiveFiltersCount() > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] text-xs font-bold rounded-full bg-emerald-600 text-white dark:bg-emerald-50 dark:text-emerald-800 border-2 border-white dark:border-slate-800">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>

          {showCities && (
            <div className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-2xl shadow-lg border border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-800">
              <div className="p-2">
                <div
                  className="flex items-center gap-3 py-2 px-3 rounded-xl cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                  onClick={handleLocationSearch}
                >
                  <Crosshair className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-sm text-slate-700 dark:text-slate-300">{t.useCurrentLocation}</span>
                </div>

                {isLoadingCities || isLoadingLocation ? (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t.loading}</span>
                  </div>
                ) : filteredCities.length > 0 ? (
                  <div className="max-h-[240px] overflow-y-auto">
                    {filteredCities.map(city => (
                      <div
                        key={city.id}
                        className="flex items-center gap-3 py-2 px-3 rounded-xl cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/30"
                        onClick={() => {
                          setLocation(city.name);
                          setLocationInput(city.name);
                          setShowCities(false);
                        }}
                      >
                        <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{city.name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <span className="text-sm text-slate-600 dark:text-slate-400">{t.noCitiesFound}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {variant === "global" ? (
        <Dialog open={showFilters} onOpenChange={setShowFilters}>
          <DialogContent className={cn("sm:max-w-[500px] rounded-2xl", isRTL ? "rtl" : "ltr")}>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                {t.filters}
                {getActiveFiltersCount() > 0 && (
                  <Badge variant="secondary" className="rounded-full">
                    {getActiveFiltersCount()} {t.selected}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            <FiltersContent
              filters={FILTERS}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              onClose={() => setShowFilters(false)}
              variant="dialog"
              locale={locale}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={showFilters} onOpenChange={setShowFilters}>
          <SheetContent side={isRTL ? "left" : "bottom"} className={cn(
            "h-[80vh] rounded-t-3xl bg-white dark:bg-slate-900",
            isRTL ? "rtl" : "ltr"
          )}>
            <SheetHeader className="border-b border-slate-200 dark:border-slate-700 pb-4">
              <SheetTitle className="text-emerald-700 dark:text-emerald-200 text-lg font-semibold">
                {t.filters}
              </SheetTitle>
            </SheetHeader>
            <FiltersContent
              filters={FILTERS}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              onClose={() => setShowFilters(false)}
              variant="sheet"
              locale={locale}
            />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

interface FiltersContentProps {
  filters: FiltersConfig;
  selectedFilters: Record<string, string | number>;
  setSelectedFilters: React.Dispatch<React.SetStateAction<Record<string, string | number>>>;
  onClose: () => void;
  variant?: "dialog" | "sheet";
  locale?: Locale;
}

function FiltersContent({
  filters,
  selectedFilters,
  setSelectedFilters,
  onClose,
  variant = "dialog",
  locale = "en"
}: FiltersContentProps) {
  const t = translations[locale as TranslationKey];
  const isRTL = locale === "ar";

  // Map from filter category key in FILTERS object to state/URL param key
  const paramKeyMap: Record<string, keyof typeof selectedFilters> = {
    placeType: 'type',
    status: 'status',
    priceRange: 'price_range',
    rating: 'min_rating'
  };

  const getActiveFiltersCount = () => {
    // Ensure 0 or empty strings don't count as active for min_rating and price_range unless intended
    let count = 0;
    if (selectedFilters.type) count++;
    if (selectedFilters.status) count++;
    if (selectedFilters.price_range) count++; // Assumes empty string is "any price"
    // Ensure min_rating is treated as a number for comparison
    if (typeof selectedFilters.min_rating === 'number' && selectedFilters.min_rating > 0) count++; // Assumes 0 is "any rating"
    return count;
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      type: "",
      status: "",
      price_range: "",
      min_rating: 0
    });
  };

  const getCategoryLabel = (categoryKey: keyof FiltersConfig): string => {
    switch (categoryKey) {
      case "placeType": return t.placeType;
      case "status": return t.status;
      case "priceRange": return t.priceRange;
      case "rating": return t.rating;
      default:
        const exhaustiveCheck: never = categoryKey;
        return exhaustiveCheck;
    }
  };

  return (
    <>
      <ScrollArea className={cn(
        variant === "sheet" ? "h-[calc(80vh-10rem)] px-1 py-4" : "max-h-[60vh] p-1"
      )}>
        <div className="space-y-6">
          {(Object.keys(filters) as Array<keyof FiltersConfig>).map((categoryKey) => {
            const options = filters[categoryKey];
            const currentParamKey = paramKeyMap[categoryKey];
            return (
              <div key={categoryKey} className="space-y-3">
                <h3 className={cn(
                  "font-semibold text-emerald-700 dark:text-emerald-200",
                  variant === "sheet" ? "text-lg" : "text-base mb-1"
                )}>
                  {getCategoryLabel(categoryKey)}
                </h3>
                <div className={cn(
                  "grid gap-3",
                  categoryKey === 'priceRange' || categoryKey === 'rating' ? "grid-cols-3" : "grid-cols-2" // More columns for price/rating
                )}>
                  {options.map((filter: any) => { // filter is an item from FiltersConfig arrays
                    const isSelected = selectedFilters[currentParamKey] === filter.value;
                    return (
                      <Button
                        key={filter.id}
                        variant="outline"
                        className={cn(
                          "justify-start gap-2.5 transition-all rounded-xl h-auto py-3 px-3 text-left items-center",
                          variant === "sheet" ? "text-sm" : "text-xs",
                          isSelected
                            ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600 hover:border-emerald-600 dark:bg-emerald-600 dark:border-emerald-600 dark:text-white dark:hover:bg-emerald-500 dark:hover:border-emerald-500"
                            : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/60",
                          // Icon color handling
                          isSelected && filter.icon ? "[&_svg]:text-white" : filter.icon ? "[&_svg]:text-emerald-600 [&_svg]:dark:text-emerald-400" : ""
                        )}
                        onClick={() => setSelectedFilters(prev => ({
                          ...prev,
                          [currentParamKey]: prev[currentParamKey] === filter.value
                            ? (currentParamKey === 'min_rating' ? 0 : "")
                            : filter.value
                        }))}
                      >
                        {filter.icon && React.cloneElement(filter.icon, { className: cn("w-4 h-4 shrink-0", isSelected ? "text-white" : "text-emerald-600 dark:text-emerald-400") })}
                        <span className="flex-1 leading-tight whitespace-nowrap overflow-hidden text-ellipsi focus:text-white dark:text-gray-200 font-semibold">
                          {filter.label}
                        </span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer with Clear All and Apply Filters buttons */}
      <div className={cn(
        "flex gap-3 items-center",
        variant === "sheet"
          ? "px-4 py-4 border-t border-slate-200 dark:border-slate-700"
          : "justify-end pt-4 border-t border-slate-200 dark:border-slate-700"
      )}>
        <Button
          variant="outline"
          onClick={handleClearFilters}
          disabled={getActiveFiltersCount() === 0}
          className={cn(
            "transition-all rounded-xl",
            variant === "sheet"
              ? "flex-1 h-12 text-base"
              : "h-10 text-sm",
            "border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700",
            getActiveFiltersCount() === 0 && "opacity-50 cursor-not-allowed"
          )}
        >
          {t.clearAll}
        </Button>
        <Button
          onClick={onClose}
          className={cn(
            "text-white transition-all rounded-xl",
            variant === "sheet"
              ? "flex-1 h-12 text-base"
              : "h-10 text-sm",
            "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
          )}
        >
          {t.applyFilters} ({getActiveFiltersCount()})
        </Button>
      </div>
    </>
  );
}